import React, { useState } from "react";
import { Mail, X, Send, Loader2, Check } from "lucide-react";
import { api, formatApiError } from "../lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/**
 * Contact admin surface.
 *
 * v3.89w — Floating FAB on every page (`<ContactAdminWidget />`).
 * v3.99c.14 — Split into:
 *   • `ContactAdminModal` (controlled, pure modal — open/onClose props)
 *   • `ContactAdminInlineButton` (inline button + its own state)
 *   • `ContactAdminWidget` (default — the original bottom-right FAB)
 *
 * The FAB is now hidden for ALL signed-in roles (collector / breeder /
 * admin) because their dashboards have their own inbox surfaces and the
 * FAB was overlapping the bottom dashboard menu bar.
 *
 * All three forms POST to /api/contact-admin, which stores the message
 * in the admin queue AND emails ADMIN_EMAIL via Resend.
 */

// ---------------------------------------------------------------------------
// Pure controlled modal — used by both the FAB and the inline button.
// ---------------------------------------------------------------------------
export function ContactAdminModal({ open, onClose }) {
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    subject: "",
    message: "",
    from_email: "",
    from_name: "",
  });

  const reset = () => {
    setForm({ subject: "", message: "", from_email: "", from_name: "" });
    setSent(false);
    setSubmitting(false);
  };

  const close = () => {
    if (submitting) return;
    onClose?.();
    // Hold the success state briefly so a subsequent open lands clean.
    setTimeout(reset, 500);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (form.subject.trim().length < 3) {
      toast.error("Subject needs to be at least 3 characters.");
      return;
    }
    if (form.message.trim().length < 10) {
      toast.error("Message needs to be at least 10 characters.");
      return;
    }
    if (!user && !/.+@.+\..+/.test(form.from_email)) {
      toast.error("Enter a valid email so we can reply.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        subject: form.subject.trim(),
        message: form.message.trim(),
      };
      if (!user) {
        payload.from_email = form.from_email.trim().toLowerCase();
        payload.from_name = form.from_name.trim();
      }
      const r = await api.post("/contact-admin", payload);
      setSent(true);
      if (r.data?.email_sent) {
        toast.success("Message sent — we'll get back to you soon.");
      } else {
        toast.success("Message received — admin will see it shortly.");
      }
      setTimeout(() => close(), 2000);
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't send. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 sm:p-6"
         onClick={close}
         data-testid="contact-admin-modal">
      <div className="bg-[var(--surface)] border border-[var(--border-strong)] w-full max-w-md p-5 sm:p-6 relative"
           style={{ borderRadius: 2 }}
           onClick={(e) => e.stopPropagation()}>
        <div className="absolute top-0 left-0 right-0 gold-line"/>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="label-eyebrow mb-1" style={{ color: "var(--gold)" }}>Contact</div>
            <h2 className="font-display text-2xl tracking-tight" style={{ color: "var(--text)" }}>
              Send us a note
            </h2>
            <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
              Goes straight to the CannaForge admin inbox. We typically reply within 24 hours.
            </p>
          </div>
          <button onClick={close} disabled={submitting}
                  className="text-[var(--text-muted)] hover:text-[var(--text)] disabled:opacity-30"
                  data-testid="contact-admin-close">
            <X className="w-5 h-5"/>
          </button>
        </div>

        {sent ? (
          <div className="py-8 text-center" data-testid="contact-admin-sent">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full border border-emerald-500/40 bg-emerald-500/10 mb-3">
              <Check className="w-7 h-7 text-emerald-300"/>
            </div>
            <div className="text-base font-semibold" style={{ color: "var(--text)" }}>Message sent.</div>
            <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
              Thanks — admin has been notified.
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            {!user && (
              <div className="grid grid-cols-1 gap-3">
                <Field label="Your email" required>
                  <input type="email" required value={form.from_email}
                         onChange={(e) => setForm({ ...form, from_email: e.target.value })}
                         placeholder="you@example.com"
                         className="input-field"
                         data-testid="contact-admin-from-email"/>
                </Field>
                <Field label="Your name (optional)">
                  <input type="text" maxLength={120} value={form.from_name}
                         onChange={(e) => setForm({ ...form, from_name: e.target.value })}
                         placeholder="How should we address you?"
                         className="input-field"
                         data-testid="contact-admin-from-name"/>
                </Field>
              </div>
            )}
            {user && (
              <div className="text-[11px] font-mono" style={{ color: "var(--text-muted)" }}>
                Replying to: <span style={{ color: "var(--text)" }}>{user.email}</span>
              </div>
            )}
            <Field label="Subject" required>
              <input type="text" required minLength={3} maxLength={140}
                     value={form.subject}
                     onChange={(e) => setForm({ ...form, subject: e.target.value })}
                     placeholder="e.g. Question about my order"
                     className="input-field"
                     data-testid="contact-admin-subject"/>
            </Field>
            <Field label="Message" required>
              <textarea required minLength={10} maxLength={4000} rows={6}
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        placeholder="What can we help with?"
                        className="input-field font-sans"
                        data-testid="contact-admin-message"/>
            </Field>
            <div className="flex items-center justify-end gap-2 pt-1">
              <button type="button" onClick={close} disabled={submitting}
                      className="btn-outline"
                      data-testid="contact-admin-cancel">Cancel</button>
              <button type="submit" disabled={submitting}
                      className="btn-primary inline-flex items-center gap-1.5"
                      data-testid="contact-admin-submit">
                {submitting
                  ? <Loader2 className="w-4 h-4 animate-spin"/>
                  : <Send className="w-4 h-4"/>}
                {submitting ? "Sending…" : "Send message"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-mono uppercase tracking-[0.18em] mb-1"
           style={{ color: "var(--text-muted)" }}>
        {label}{required && <span className="ml-0.5 text-[var(--gold)]">*</span>}
      </div>
      {children}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Inline button — drop into any dashboard surface (e.g. Messages area).
// ---------------------------------------------------------------------------
export function ContactAdminInlineButton({
  label = "Contact admin",
  className = "btn-primary inline-flex items-center gap-2 text-xs",
  testId = "contact-admin-inline",
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={className}
        data-testid={testId}
      >
        <Mail className="w-4 h-4" />
        {label}
      </button>
      <ContactAdminModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Default export — the original floating FAB. Hidden for signed-in users
// so it doesn't fight the dashboard's bottom menu bar.
// ---------------------------------------------------------------------------
export default function ContactAdminWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  // v3.99c.14 — collectors / breeders / admins use the dedicated
  // "Contact admin" button inside their Messages area instead.
  if (user) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 px-4 py-3 bg-[var(--gold)] text-black shadow-lg hover:scale-105 active:scale-95 transition-transform text-xs font-mono uppercase tracking-[0.18em]"
        style={{ borderRadius: 2 }}
        data-testid="contact-admin-launcher"
        aria-label="Contact admin"
      >
        <Mail className="w-4 h-4" />
        <span className="hidden sm:inline">Contact</span>
      </button>
      <ContactAdminModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}
